import React, { useEffect, useRef, useState } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';

import { PrimaryButton, SecondaryButton } from '../../../../components/Button';
import EmptyState from '../../../../components/Card/EmptyState';
import { Modal } from '../../../../components/Modals/Modal';
import ApplyForJobModal from '../../../../components/Modals/ModalsActions/ApplyForJobModal';
import ReportJobModal from '../../../../components/Modals/ModalsActions/ReportJobModal';
import ModalTriggerContainer from '../../../../components/Modals/ModalTriggerContainer';
import { ModalProvider } from '../../../../context/ModalContext';
import useGetSingleBookmarkedLocumJob from '../../../../hooks/dashboard/jobs/useGetSingleBookmarkedLocumJob';
import useQueryString from '../../../../hooks/ui-control/useQueryString';
import { useAppSelector, useAppDisPatch } from '../../../../redux/store';
import { locumSingleBookmarkJobsActions } from '../../../../redux/slices/locumSingleBookmarkJobSlice';
import BookmarkJobInfoBody from './BookmarkJobInfoBody';
import BookmarkJobInfoHead from './BookmarkJobInfoHead';
import JobInfoSkeleton from '../../../../components/Skeletons/JobInfoSkeleton';

interface BookmarkJobInfoProps {
  showMobileInfo?: boolean;
  reset?: any;
}

const BookmarkJobInfo: React.FC<BookmarkJobInfoProps> = ({
  showMobileInfo,
  reset,
}) => {
  const [queryParams, setQueryParams] = useQueryString();
  const { getSingleBookmarkedLocumJob, loading } =
    useGetSingleBookmarkedLocumJob();
  const { bookmarkJobData, jobId: existingJobId } = useAppSelector(
    (state) => state.locumSingleBookmarkJobs
  );
  const dispatch = useAppDisPatch();

  const jobId = queryParams.get('jobId');

  // Track previous reset value
  const prevReset = useRef(reset);

  // 4) One “ready” flag for skeleton
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (existingJobId && jobId) {
      setQueryParams({ jobId: existingJobId });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (jobId && jobId !== existingJobId) {
      // Clear previous job immediately
      dispatch(locumSingleBookmarkJobsActions.clearSingleBookmarkJob());
      // Then fetch the new job
      getSingleBookmarkedLocumJob(jobId);
    }
    // eslint-disable-next-line
  }, [jobId]);

  // Clear job info when `reset` changes (pagination reset)
  useEffect(() => {
    if (prevReset.current !== reset) {
      dispatch(locumSingleBookmarkJobsActions.clearSingleBookmarkJob());
      setQueryParams({ jobId: '' });
      prevReset.current = reset;
    }
  }, [reset, dispatch, setQueryParams]);

  async function allReady(): Promise<boolean> {
    const dataReady =
      !loading && bookmarkJobData != null && bookmarkJobData.id === jobId;
    if (!dataReady) {
      return false;
    }
    return true;
  }

  useEffect(() => {
    setIsReady(false);

    allReady().then((ok) => {
      setIsReady(ok);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, bookmarkJobData, jobId]);

  if (!jobId) {
    return (
      <EmptyState className="no-small-screen">
        Click on a job to preview
      </EmptyState>
    );
  }

  if (!isReady) {
    return (
      <StickyWrapper className={showMobileInfo ? 'showMobileInfo' : ''}>
        <Container>
          <JobInfoSkeleton />
        </Container>
      </StickyWrapper>
    );
  }

  return (
    <StickyWrapper className={showMobileInfo ? 'showMobileInfo' : ''}>
      <Container>
        <BookmarkJobInfoHead
          imgSrc={'/static/gif/happyAnimal.gif'}
          title={bookmarkJobData?.title ?? ''}
          institutionName={bookmarkJobData?.institutionName ?? ''}
          yearsOfExperience={`${
            bookmarkJobData?.yearsOfExperience ?? ''
          } years+`}
          location={bookmarkJobData?.location ?? ''}
          pay={bookmarkJobData?.pay ?? 0}
          payInterval={bookmarkJobData?.payInterval ?? ''}
          jobStartDate={bookmarkJobData?.jobStartDate ?? ''}
          jobEndDate={bookmarkJobData?.jobEndDate ?? ''}
          jobId={bookmarkJobData?.id ?? ''}
        />

        <BookmarkJobInfoBody jobDescription={bookmarkJobData?.jobDescription} />

        <ButtonArea>
          <ModalProvider>
            <ModalTriggerContainer>
              <SecondaryButton className="fw600 pl-pr-4 respond">
                {loading ? <LoadingOutlined /> : 'Apply'}
              </SecondaryButton>
            </ModalTriggerContainer>

            <Modal>
              <ApplyForJobModal
                jobId={jobId}
                jobCreatorId={bookmarkJobData?.jobCreatorId}
              />
            </Modal>
          </ModalProvider>

          <ModalProvider>
            <ModalTriggerContainer>
              <PrimaryButton className="fw600 danger respond">
                Report
              </PrimaryButton>
            </ModalTriggerContainer>

            <Modal>
              <ReportJobModal
                title={bookmarkJobData?.title ?? ''}
                description={bookmarkJobData?.institutionName ?? ''}
                jobId={jobId}
              />
            </Modal>
          </ModalProvider>
        </ButtonArea>
      </Container>
    </StickyWrapper>
  );
};

export default BookmarkJobInfo;

const StickyWrapper = styled.div`
  display: flex;
  position: sticky;
  top: 0;
  height: 80vh;
  overflow: auto;
  scroll-bar: hidden;

  @media (max-width: 884px) {
    display: none;
    &.showMobileInfo {
      display: grid;
    }
  }
`;

const Container = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 6px;
  padding: 32px;
  display: grid;
  gap: 16px;
  height: fit-content;
  width: 100%;
  background-color: ${({ theme }) => theme.palette.white};

  @media (max-width: 540px) {
    padding: 1rem;
    border-radius: 0;
    border: none;
  }
`;

const ButtonArea = styled.div`
  display: flex;
  gap: 2rem;
  height: fit-content;
`;
